/**
 * Random Number Generator Tool
 *
 * Generate random numbers with configurable range, count, and precision.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

function generateRandom(min: number, max: number, decimals: number): number {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  const value = min + (array[0] / 0xFFFFFFFF) * (max - min);
  if (decimals === 0) return Math.round(value);
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

export const RandomNumberGeneratorTool: IGeneratorTool = {
  id: 'random-number-generator',
  name: 'Random Number Generator',
  description: 'Generate random numbers with configurable range',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['random', 'number', 'generate', 'dice', 'rng'],

  seo: {
    slug: 'random-number-generator',
    pageTitle: 'Random Number Generator - Free Online Tool',
    metaDescription: 'Generate random numbers with custom range, decimal places, and uniqueness. Cryptographically random using Web Crypto API. Runs in your browser.',
    keywords: ['random number generator', 'rng', 'random integer', 'random decimal', 'dice roller online'],
    relatedTools: ['random-string-generator', 'uuid-generator'],
    schemaType: 'WebApplication',
  },

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'min',
      type: 'number',
      label: 'Minimum',
      defaultValue: 1,
      validation: { min: -1000000000, max: 1000000000 },
      description: 'Minimum value (inclusive)',
    },
    {
      name: 'max',
      type: 'number',
      label: 'Maximum',
      defaultValue: 100,
      validation: { min: -1000000000, max: 1000000000 },
      description: 'Maximum value (inclusive)',
    },
    {
      name: 'count',
      type: 'number',
      label: 'Count',
      defaultValue: 1,
      validation: { min: 1, max: 1000 },
      description: 'Number of values to generate',
    },
    {
      name: 'decimals',
      type: 'number',
      label: 'Decimal Places',
      defaultValue: 0,
      validation: { min: 0, max: 10 },
      description: 'Number of decimal places (0 = integers)',
    },
    {
      name: 'unique',
      type: 'boolean',
      label: 'Unique',
      defaultValue: false,
      description: 'Ensure all generated numbers are unique',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const min = Number(config.min ?? 1);
    const max = Number(config.max ?? 100);
    const count = Math.max(1, Math.min(1000, Number(config.count ?? 1)));
    const decimals = Math.max(0, Math.min(10, Number(config.decimals ?? 0)));
    const unique = config.unique === true;

    if (min > max) {
      return `Error: Minimum (${min}) cannot be greater than maximum (${max})`;
    }

    if (unique && decimals === 0) {
      const range = max - min + 1;
      if (count > range) {
        return `Error: Cannot generate ${count} unique integers in range ${min}–${max} (only ${range} possible values)`;
      }
    }

    const results: number[] = [];
    const seen = new Set<number>();
    let attempts = 0;
    const maxAttempts = count * 100;

    while (results.length < count && attempts < maxAttempts) {
      const value = generateRandom(min, max, decimals);
      attempts++;

      if (unique) {
        if (!seen.has(value)) {
          seen.add(value);
          results.push(value);
        }
      } else {
        results.push(value);
      }
    }

    if (results.length < count) {
      return `Error: Could not generate ${count} unique values after ${maxAttempts} attempts`;
    }

    return results.map(n => decimals > 0 ? n.toFixed(decimals) : String(n)).join('\n');
  },
};
